import React, {useCallback, useEffect, useState, useRef} from 'react';
import styled from 'styled-components';
import {DragDropContext} from 'react-beautiful-dnd';
import {Waypoint} from 'react-waypoint';
import useResizeObserver from 'use-resize-observer';
import {IntercomAPI} from 'react-intercom';
import debounce from 'lodash.debounce';
import API from '@aws-amplify/api';

import topRight from '../assets/top_right_32.png';
import topLeft from '../assets/top_left_32.png';
import {toast, Slide} from 'react-toastify';
import {
  addWeeks,
  addDays,
  addMonths,
  subMonths,
  startOfWeek,
  format,
  startOfMonth,
  getDaysInMonth,
  getDate,
  getWeekOfMonth,
  isBefore,
  startOfDay,
  parse,
  endOfMonth
} from 'date-fns';

import {resetScrollPosition} from '../../PageContainer';

import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {useModalContext} from '@bootcamp/web/src/contexts/Modal';

import CalendarDay from './CalendarDay';
import {
  Calendar,
  CalendarWeek,
  CalendarShadow,
  CalendarContainer,
  CalendarHeaderContainer,
  CalendarHeaderRow,
  CalendarHeaderButtonsContainer,
  CalendarHeaderMonth,
  CalenderHeaderDayRow,
  CalendarHeaderDay,
  MobileCalendarDay,
  UpdateComplete
} from './shared';

import {getWeeksToDisplay} from '../helpers';
import {ChevronUp} from '@styled-icons/fluentui-system-filled/ChevronUp';
import {TargetArrow} from '@styled-icons/fluentui-system-filled/TargetArrow';
import {RoundButton} from '@bootcamp/web/src/components/UI';
import {Wand} from '@styled-icons/fluentui-system-filled/Wand';
import {Settings} from '@styled-icons/fluentui-system-filled/Settings';

const StyledCalendarWeek = styled(CalendarWeek)`
  padding-left: 36px;
  position: relative;
  background: ${({theme}) => theme.colors.neutralsPalette.offWhite};
  ${({theme}) => theme.mediaQueries.tablet} {
    padding-left: 0;
  }
`;
const StyledCalendarHeaderDayRow = styled(CalenderHeaderDayRow)`
  border-top: 1px solid ${({theme}) => theme.colors.neutralsPalette.light};
  border-bottom: 1px solid ${({theme}) => theme.colors.neutralsPalette.light};
  padding-left: 36px;
`;
const StyledCalendarHeaderDay = styled(CalendarHeaderDay)`
  border-left: 1px solid ${({theme}) => theme.colors.neutralsPalette.light};
`;

const StyledCalendarHeaderMonth = styled(CalendarHeaderMonth)`
  margin: 0;
  font-weight: 600;
  width: 91px;
  margin-right: 8px;
  ${({theme}) => theme.mediaQueries.tablet} {
    width: 100%;
  }
`;

const ChevronButton = styled(ChevronUp)`
  cursor: pointer;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  height: 32px;
  width: 32px;
  font-size: 14px;
  padding: 8px;
  gap: 0px;
  &:hover {
    background: #EDEDF0;
    border-radius: 100px;
  }
`
const CalendarButton = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0px 12px;

  background: #FFFFFF;
  border: 1px solid #EDEDF0;
  box-shadow: 0px 1px 0px #C8C9CE;
  border-radius: 256px;

  font-family: proxima-nova;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;

  text-align: center;

  color: #2F3037;
  cursor: pointer;
  &:hover {
    background: #F7F7F8;
  }
`;
const MobileCalendarButton = styled(CalendarButton)`
  padding: 8px;
  width: 32px;
`;
const MobileCalendarHeaderRow = styled(CalendarHeaderRow)`
  padding: ${({theme}) => theme.layouts.spacing.s} ${({theme}) => theme.layouts.spacing.ms};
`;
const MobileProgressRow = styled(MobileCalendarHeaderRow)`
  border-bottom: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  margin-bottom: 8px;
`;
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.04) 0%, rgba(0, 0, 0, 0.02) 100%);
  box-shadow: 0px 4px 8px 0px #0000000F inset;
  position: relative;
  border-radius: 8px;
`;
const ProgressBar = styled.div`
  width: ${({progress}) => progress}%;
  height: 100%;
  background: ${({theme}) => theme.colors.brandPalette.blue.gradient};
  border-radius: 8px;
  position: absolute;
  top: 0;
  left: 0;
  transition: width 0.5s;
  overflow: hidden;
`;
const VerticalWeekCountLabelContainer = styled.div`
  position: absolute;
  display: flex;
  top: 0;
  bottom: 0;
  transform: rotate(-90deg);

  ${({theme}) => theme.mediaQueries.tablet} {
    display: none;
  }
`;
const VerticalWeekCountLabel = styled.div`
  font-family: proxima-nova;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 100%;
  text-align: center;
  color: #C8C9CE;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  width: 36px;
`;
const ContainerRadius = styled.img`
  position: absolute;
  top: 0px;
  width: 8px;
  height: 8px;
`;
const TopRightRadius = styled(ContainerRadius)`
  right: 0;
`;
const TopLeftRadius = styled(ContainerRadius)`
  left: 0px;
`;


const Day = ({
  date,
  defaultDate,
  eventsByDate,
  examDate,
  width,
  firstDay,
  updateEventsByDate,
  selectedDate,
  setSelectedDate,
  membershipExpirationDate,
  updateAssignmentDate,
  updateEvent,
  createAssignmentOnDate,
  isFinalWeekOfMonth,
  // updateCompleteStatus,
}) => {
  const {searchUserInteractions} = useUserDataContext();
  const formattedDate = format(date, 'yyyy-MM-dd');
  const isPresent = (formattedDate >= format(new Date(), 'yyyy-MM-dd') || formattedDate >= firstDay) && formattedDate <= examDate;
  const dayEvents = eventsByDate?.[formattedDate];

  const updateCompleteStatus = (id, complete) => {
    // if the event is the final event of the day, and formatted date is today, show a toast
    const updatedEvents = eventsByDate[formattedDate].map(event => {
      if (event.id === id) {
        event.complete = complete;
      }
      return event;
    });

    if (complete && formattedDate === format(new Date(), 'yyyy-MM-dd') && updatedEvents.every(event => event.complete)) {
      toast(<UpdateComplete themePalette="green" title="All done for today!" body="Great work." showDismiss={false} />, {
        toastId: 'update-complete',
        position: 'bottom-center',
        autoClose: 2000,
        hideProgressBar: true,
        draggableDirection: 'y',
        draggablePercent: 100,
        transition: Slide,
        closeButton: false,
        style: {
          padding: 0,
        }
      });
    }


    updateEventsByDate({
      ...eventsByDate,
      [formattedDate]: updatedEvents,
    });
  }

  if (width <= 768) {
    const isSelected = date.getTime() === selectedDate.getTime();
    const isSelectable = isBefore(addDays(new Date(firstDay), -1), new Date(formattedDate)) && isBefore(addDays(new Date(formattedDate), -1), new Date(examDate));
    const hasAssignments = dayEvents?.length;

    const handleClick = () => {
      if (isSelectable || hasAssignments) setSelectedDate(date);
    };

    return (
      <MobileCalendarDay
        onClick={handleClick}
        isPresent={isPresent}
        isToday={formattedDate === format(new Date(), 'yyyy-MM-dd')}
        isExamDay={formattedDate === examDate}
        isSelected={isSelected}
        isDayOff={!dayEvents?.length}
        children={getDate(date)}
        />
    )
  }

  const dividerLineState = !isFinalWeekOfMonth ? false : date.getDate() === 1 ? 'left' : date.getDate() < 7 ? 'top' : 'bottom';
  return (
    <CalendarDay
      key={date}
      date={date}
      isPresent={isPresent}
      events={dayEvents}
      isExamDate={formattedDate === examDate}
      updateCompleteStatus={updateCompleteStatus}
      isExpirationDate={membershipExpirationDate && formattedDate === format(membershipExpirationDate, 'yyyy-MM-dd')}
      membershipExpirationDate={membershipExpirationDate}
      documentWidth={width}
      startDate={firstDay}
      examDate={examDate}
      updateAssignmentDate={updateAssignmentDate}
      updateEvent={updateEvent}
      createAssignmentOnDate={createAssignmentOnDate}
      dividerLineState={dividerLineState}
    />
  );
};

const Week = ({
  weekStart,
  defaultDate,
  firstDay,
  setScrolledMonth,
  eventsByDate,
  examDate,
  endDate,
  width,
  updateEventsByDate,
  selectedDate,
  setSelectedDate,
  membershipExpirationDate,
  updateCompleteStatus,
  updateAssignmentDate,
  updateEvent,
  createAssignmentOnDate,
}) => {
  const weekOfMonth = getWeekOfMonth(addDays(weekStart, 6));
  const weeksSinceFirstDay = Math.ceil((weekStart - parse(firstDay, 'yyyy-MM-dd', new Date())) / (1000 * 60 * 60 * 24 * 7)) + 1;

  // if the week is beyond the end date, return
  if (weekStart > endOfMonth(parse(endDate, 'yyyy-MM-dd', new Date()))) return null;


  const isFinalWeekOfMonth = getWeekOfMonth(weekStart) === getWeekOfMonth(endOfMonth(weekStart));
  return (
    <Waypoint viewportTop={72} key={`${weekStart}-waypoint`} onPositionChange={({previousPosition, currentPosition}) => {
      const scrollParent = document.getElementById('page-container');
      if (previousPosition && currentPosition === 'inside' && scrollParent.scrollTop > 0) {
        setScrolledMonth(startOfMonth(weekStart));
      }
    }}>
      <StyledCalendarWeek>
        <VerticalWeekCountLabelContainer>
          <VerticalWeekCountLabel>{weeksSinceFirstDay > 0 && weekStart <= parse(endDate, 'yyyy-MM-dd', new Date()) && `Week ${weeksSinceFirstDay}`}</VerticalWeekCountLabel>
        </VerticalWeekCountLabelContainer>
        {Array.from({length: 7}, (_, dayIndex) => {
          return (
            <Day
              key={weekStart + dayIndex}
              defaultDate={defaultDate}
              date={addDays(weekStart, dayIndex)}
              eventsByDate={eventsByDate}
              examDate={examDate}
              width={width}
              firstDay={firstDay}
              updateEventsByDate={updateEventsByDate}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              membershipExpirationDate={membershipExpirationDate}
              updateAssignmentDate={updateAssignmentDate}
              updateEvent={updateEvent}
              createAssignmentOnDate={createAssignmentOnDate}
              updateCompleteStatus={updateCompleteStatus}
              isFinalWeekOfMonth={isFinalWeekOfMonth}
            />
          );
        })}
      </StyledCalendarWeek>
    </Waypoint>
  )
};


const CalendarComponent = ({ eventsByDate, setEventsByDate, totalEvents, completedEvents, daysUntilExam, onClickRebalance, onClickSettings }) => {
  const {DEFAULT_USER_ID, bootcamp, searchUserInteractions, userModel} = useUserDataContext();
  const mobileDateRef = useRef();
  const {width} = useResizeObserver({ref: window.document.body});
  const startWeekOn = searchUserInteractions(`startWeekOn`, 0);

  const firstDayOfWeek = startWeekOn;
  const firstDay = Object.entries(eventsByDate).sort((a, b) => new Date(a?.[0]) - new Date(b?.[0])).filter(([date, value]) => value.length > 0)?.[0]?.[0];
  const todayFormatted = format(new Date(), 'yyyy-MM-dd')
  const firstDayIncomplete = Object.entries(eventsByDate).sort((a, b) => new Date(a?.[0]) - new Date(b?.[0])).filter(([date, value]) => value.length > 0 && value.some(el => !el.complete))?.[0]?.[0] || todayFormatted;

  const firstDayOfSchedule = parse(firstDay, 'yyyy-MM-dd', new Date());
  const initialDisplayDate = parse(firstDayIncomplete, 'yyyy-MM-dd', new Date());

  const [defaultDate, setDefaultDate] = useState(initialDisplayDate);
  const [scrolledMonth, setScrolledMonth] = useState(initialDisplayDate);
  const [selectedDate, setSelectedDate] = useState(startOfDay(new Date()));
  const examDate = searchUserInteractions(`testDate`, null);

  const membershipExpirationDate = userModel?.membershipExpirationDate ? new Date(userModel?.membershipExpirationDate * 1000) : null;
  const lastDayContainingEvents = Object.keys(eventsByDate).sort((a, b) => new Date(b) - new Date(a))[0];
  const endDate = new Date(examDate) > new Date(lastDayContainingEvents) ? examDate : lastDayContainingEvents;

  const [weeks, setWeeks] = useState(getWeeksToDisplay(defaultDate));
  useEffect(() => {
    if (width <= 768) {
      handleToday();
      setWeeks(getWeeksToDisplay(new Date()));
    }
  }, [width]);

  const handleToday = () => {
    setDefaultDate(new Date());
    setSelectedDate(startOfDay(new Date()));
    setScrolledMonth(startOfMonth(new Date()));
    resetScrollPosition();
    if (mobileDateRef.current) {
      mobileDateRef.current.value = startOfMonth(new Date());
    }
  };

  const handleBack = () => {
    const prevMonth = startOfMonth(subMonths(scrolledMonth, 1));
    const element = document.getElementsByClassName(format(prevMonth, 'yyyy-MM'))[0];
    if (!element) {
      if (scrolledMonth < startOfMonth(firstDayOfSchedule)) return;
      if (prevMonth < startOfMonth(firstDayOfSchedule)) {
        setScrolledMonth(startOfMonth(firstDayOfSchedule));
        return setDefaultDate(startOfMonth(firstDayOfSchedule));
      }
      setScrolledMonth(prevMonth);
      return setDefaultDate(prevMonth);
    };
    const elementDimensions = element.getBoundingClientRect();
    const scrollParent = document.getElementById('page-container');
    scrollParent.scrollTop += elementDimensions.top - scrollParent.getBoundingClientRect().top - 116;
    setScrolledMonth(prevMonth);

  };

  const handleForward = () => {
    // if we're beyond the exam day, return
    if (addMonths(scrolledMonth, 1) >= parse(examDate, 'yyyy-MM-dd', new Date())) return;
    const nextMonth = startOfMonth(addMonths(scrolledMonth, 1));
    const element = document.getElementsByClassName(format(nextMonth, 'yyyy-MM'))[0];
    if (!element) return;
    const elementDimensions = element.getBoundingClientRect();
    const scrollParent = document.getElementById('page-container');

    scrollParent.scrollTop += elementDimensions.top - scrollParent.getBoundingClientRect().top - 116;
    setScrolledMonth(nextMonth);
    // add more weeks if necessary
    const weeksToAdd = Math.ceil((getDaysInMonth(nextMonth) - getDate(nextMonth)) / 7) + 1;
    setWeeks(weeks + weeksToAdd);
  };

  const updateEventsByDate = useCallback(async (events) => {
    setEventsByDate(events);
    await API.graphql({query:`
      mutation updateQuickCache($input: UpdateQuickCacheInput!) {
        updateQuickCache(input: $input) {
          id
        }
      }
    `, variables: {input: {id: `${DEFAULT_USER_ID}-${bootcamp}-study-schedule`, content: JSON.stringify(events)}}, authMode: 'AMAZON_COGNITO_USER_POOLS'});


    const totalEventsComplete = Object.values(events).reduce((acc, events) => {
      return acc + events.filter(event => event.complete).length;
    }, 0);

    // update events in intercom
    IntercomAPI('update', {completedAssignments: totalEventsComplete});

  }, [DEFAULT_USER_ID, JSON.stringify(eventsByDate)]);

  const updateCompleteStatus = (id, complete) => {
    const updatedEvents = eventsByDate[format(selectedDate, 'yyyy-MM-dd')].map(event => {
      if (event.id === id) {
        event.complete = complete;
      }
      return event;
    });
    updateEventsByDate({
      ...eventsByDate,
      [format(selectedDate, 'yyyy-MM-dd')]: updatedEvents,
    });
  };

  const createAssignmentOnDate = (date, event, isDuplicate) => {
    if (isDuplicate) {
      // show success toast
      toast(<UpdateComplete themePalette="royal" iconName="copy" title="Assignment duplicated" body="The assignment has been duplicated." showDismiss={false} />, {
        toastId: `duplicate-complete-${event.id}`,
        position: 'bottom-center',
        autoClose: 2000,
        hideProgressBar: true,
        draggableDirection: 'y',
        draggablePercent: 100,
        transition: Slide,
        closeButton: false,
        style: {
          padding: 0,
        }
      });
    }
    updateEventsByDate({
      ...eventsByDate,
      [date]: [...eventsByDate[date] || [], event],
    });
  }

  const updateAssignmentDate = (id, date, newDate) => {
    const event = eventsByDate[date].find(event => event.id === id);
    const updatedEvents = eventsByDate[date].filter(event => event.id !== id);
    updateEventsByDate({
      ...eventsByDate,
      [date]: updatedEvents,
      [newDate]: [...eventsByDate[newDate] || [], event],
    });
  }

  const updateEvent = (id, date, event, save=true) => {
    if (!event) {
      // first, save a rollback
      const rollbackEvents = {...eventsByDate};
      const updatedEvents = eventsByDate[format(date, 'yyyy-MM-dd')].filter(e => e.id !== id);
      updateEventsByDate({
        ...eventsByDate,
        [format(date, 'yyyy-MM-dd')]: updatedEvents,
      });
      const eventDetails = rollbackEvents[format(date, 'yyyy-MM-dd')].find(e => e.id === id);
      toast(<UpdateComplete themePalette="red" iconName="reply" title="Assignment deleted" body={<div><span style={{textDecoration: 'underline'}} onClick={() => {
        updateEventsByDate(rollbackEvents);
        toast.dismiss(`delete-complete-${id}`);
      }}>Undo</span> to recover "{eventDetails.title?.slice(0,12) || "New assignment"}{eventDetails.title?.length > 12 ? '...' : ''}"</div>} showDismiss />, {
        toastId: `delete-complete-${id}`,
        position: 'bottom-center',
        autoClose: 5000,
        hideProgressBar: true,
        draggableDirection: 'y',
        draggablePercent: 100,
        transition: Slide,
        closeButton: false,
        style: {
          padding: 0,
        }
      });
      return;
    }
    const updatedEvents = eventsByDate[format(date, 'yyyy-MM-dd')].map(e => {
      if (e.id === id) {
        return event;
      }
      return e;
    });
    if (save) {
      updateEventsByDate({
        ...eventsByDate,
        [format(date, 'yyyy-MM-dd')]: updatedEvents,
      });
    } else {
      setEventsByDate({
        ...eventsByDate,
        [format(date, 'yyyy-MM-dd')]: updatedEvents,
      })
    }
  }

  const onDragEnd = ({source, destination, draggableId}) => {
    if (!destination) return;

    const sourceEvents = Array.from(eventsByDate[source.droppableId]);
    const movedEventIndex = sourceEvents.findIndex(event => event.id === draggableId);
    const [movedEvent] = sourceEvents.splice(movedEventIndex, 1);

    if (source.droppableId === destination.droppableId) {
      sourceEvents.splice(destination.index, 0, movedEvent);
      return updateEventsByDate({
        ...eventsByDate,
        [source.droppableId]: sourceEvents,
      });
    }

    const destinationEvents = Array.from(eventsByDate[destination.droppableId] || []);
    destinationEvents.splice(destination.index, 0, movedEvent);
    updateEventsByDate({
      ...eventsByDate,
      [source.droppableId]: sourceEvents,
      [destination.droppableId]: destinationEvents,
    });
  };

  const handleMonthChange = (e) => {
    const month = e?.target?.value;
    mobileDateRef.current.value = month;
    setDefaultDate(month);
    setWeeks(getWeeksToDisplay(month));
  };

  useEffect(() => {
    setScrolledMonth(startOfMonth(defaultDate))
  }, [defaultDate]);

  useEffect(() => {
    // make sure mobile calendar displays properly when resizing the window
    const handleResize = () => {
      setWeeks(getWeeksToDisplay(defaultDate));
    };

    const debouncedHandleResize = debounce(handleResize, 400);

    window.addEventListener('resize', debouncedHandleResize);

    return () => {
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, [defaultDate]);

  if (!examDate) return null;

  const weeksDates = Array.from({length: weeks}, (_, index) => addWeeks(startOfWeek(defaultDate, {weekStartsOn: firstDayOfWeek}), index));
  const renderedWeeks = weeksDates.map((weekStart, index) => (
    <Week
      key={weekStart}
      defaultDate={defaultDate}
      weekStart={weekStart}
      weekIndex={index + 1}
      firstDay={firstDay}
      setScrolledMonth={setScrolledMonth}
      eventsByDate={eventsByDate}
      examDate={examDate}
      width={width}
      updateEventsByDate={updateEventsByDate}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
      membershipExpirationDate={membershipExpirationDate}
      endDate={endDate}
      updateCompleteStatus={updateCompleteStatus}
      updateAssignmentDate={updateAssignmentDate}
      updateEvent={updateEvent}
      createAssignmentOnDate={createAssignmentOnDate}
    />
  ));

  if (width <= 768) {
    return (
      <CalendarContainer>
        <CalendarHeaderContainer>
          <MobileCalendarHeaderRow>
            <StyledCalendarHeaderMonth>
              <MobileDateSelect ref={mobileDateRef} defaultValue={defaultDate} onChange={handleMonthChange}>
                {Array.from({length: 12}, (_, index) => {
                  const date = addMonths(startOfMonth(initialDisplayDate), index);
                  const paddedExamDate = parse(examDate, 'yyyy-MM-dd', new Date());
                  const disabled = isBefore(new Date(paddedExamDate), new Date(date))
                  return <option key={date} value={date} disabled={disabled}>{format(date, 'MMM yyyy')}</option>
                })}
              </MobileDateSelect>
              </StyledCalendarHeaderMonth>
            <CalendarHeaderButtonsContainer>
              <CalendarButton onClick={handleToday}>Today</CalendarButton>
              <MobileCalendarButton onClick={onClickRebalance}>
                <Wand size={16} color="#2F3037" />
              </MobileCalendarButton>
              <MobileCalendarButton onClick={onClickSettings}>
                <Settings size={16} color="#2F3037" />
              </MobileCalendarButton>
            </CalendarHeaderButtonsContainer>
          </MobileCalendarHeaderRow>
          <MobileProgressRow style={{paddingTop: 0}}>
            <PillContainer>
              <Pill style={{width: '100%'}}>
                <ProgressBarContainer>
                  <ProgressBar progress={completedEvents / totalEvents * 100} />
                </ProgressBarContainer>
                {completedEvents} / {totalEvents}
              </Pill>
              <Pill>
                <TargetArrow size={16} />{daysUntilExam} days until exam
              </Pill>
            </PillContainer>
          </MobileProgressRow>
          <CalenderHeaderDayRow>
            {Array.from({ length: 7 }, (_, index) => (
              <CalendarHeaderDay children={format(addDays(startOfWeek(defaultDate, {weekStartsOn: firstDayOfWeek}), index), 'E')} />
            ))}
          </CalenderHeaderDayRow>
        </CalendarHeaderContainer>
        <DragDropContext onDragEnd={onDragEnd}>
          <Calendar>
            {renderedWeeks}
          </Calendar>
          <CalendarDay
            documentWidth={width}
            examDate={examDate}
            isExamDate={format(selectedDate, 'yyyy-MM-dd') === examDate}
            startDate={firstDay}
            date={selectedDate}
            isPresent={selectedDate >= startOfDay(new Date())}
            events={eventsByDate[format(selectedDate, 'yyyy-MM-dd')]}
            updateCompleteStatus={updateCompleteStatus}
            updateAssignmentDate={updateAssignmentDate}
            updateEvent={updateEvent}
            createAssignmentOnDate={createAssignmentOnDate}
          />
        </DragDropContext>
      </CalendarContainer>
    )
  }

  return (
    <CalendarShadow>
      <CalendarContainer>
        <CalendarHeaderContainer>
          <TopLeftRadius src={topLeft}/>
          <TopRightRadius src={topRight}/>
          <CalendarHeaderRow>
            <StyledCalendarHeaderMonth>{format(scrolledMonth, 'MMM yyyy')}</StyledCalendarHeaderMonth>
            <CalendarHeaderButtonsContainer style={{marginLeft: '0', marginRight: 'auto'}}>
              <CalendarButton onClick={handleToday}>Today</CalendarButton>
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '-4px'}}>
                <ChevronButton onClick={handleBack} />
                <ChevronButton style={{transform: 'rotate(180deg)'}} onClick={handleForward} />
              </div>
            </CalendarHeaderButtonsContainer>
            <CalendarHeaderButtonsContainer>
              <CalendarButton onClick={onClickRebalance}>
                <Wand size={16} color="#2F3037" /> Rebalance
              </CalendarButton>
              <CalendarButton style={{width: 32, padding: '0px 7px'}} onClick={onClickSettings}>
                <Settings size={16} color="#2F3037" />
              </CalendarButton>
            </CalendarHeaderButtonsContainer>
          </CalendarHeaderRow>
          <CalendarHeaderRow style={{paddingTop: 0}}>
            <PillContainer>
              <Pill style={{width: '100%'}}>
                <ProgressBarContainer>
                  <ProgressBar progress={completedEvents / totalEvents * 100} />
                </ProgressBarContainer>
                {completedEvents} / {totalEvents} assignments completed
              </Pill>
              <Pill>
                <TargetArrow size={16} />{daysUntilExam} days until exam
              </Pill>
            </PillContainer>
          </CalendarHeaderRow>
          <StyledCalendarHeaderDayRow>
            {Array.from({ length: 7 }, (_, index) => (
              <StyledCalendarHeaderDay children={format(addDays(startOfWeek(defaultDate, {weekStartsOn: firstDayOfWeek}), index), 'E')} />
            ))}
          </StyledCalendarHeaderDayRow>
        </CalendarHeaderContainer>
        <DragDropContext onDragEnd={onDragEnd}>
          <Calendar id={'calendar-container'}>
            {renderedWeeks}
            <Waypoint key={initialDisplayDate} onEnter={() => {
              // if we're beyond the exam day, return
              const nextMonth = addWeeks(weeksDates[weeks - 1], 1);
              if (nextMonth > parse(endDate, 'yyyy-MM-dd', new Date())) return;

              const firstDateNextMonth = getDate(nextMonth) - 1;
              const newDaysInNextMonth = getDaysInMonth(nextMonth) - firstDateNextMonth;
              const weeksToAdd = Math.ceil((newDaysInNextMonth) / 7) + 1;
              setWeeks(weeks + weeksToAdd);
            }} />
          </Calendar>
        </DragDropContext>
      </CalendarContainer>
    </CalendarShadow>
  );
};

const MobileDateSelect = styled.select`
  appearance: none;
  border: none;
  font-family: proxima-nova;
  font-weight: 700;
  font-size: 16px;
  line-height: 125%;
  background: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTciIGhlaWdodD0iMTciIHZpZXdCb3g9IjAgMCAxNyAxNyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTMuNDM4MjIgNS4zMzA4QzMuNTMxOTcgNS4yMzcxNiAzLjY1OTA1IDUuMTg0NTcgMy43OTE1NSA1LjE4NDU3QzMuOTI0MDUgNS4xODQ1NyA0LjA1MTE0IDUuMjM3MTYgNC4xNDQ4OSA1LjMzMDhMOC42MjQ4OSA5LjgxMDhMMTMuMTA0OSA1LjMzMDhDMTMuMTk5NyA1LjI0MjQ4IDEzLjMyNSA1LjE5NDM5IDEzLjQ1NDYgNS4xOTY2OEMxMy41ODQxIDUuMTk4OTcgMTMuNzA3NyA1LjI1MTQ0IDEzLjc5OTMgNS4zNDMwNUMxMy44OTA5IDUuNDM0NjYgMTMuOTQzNCA1LjU1ODI1IDEzLjk0NTcgNS42ODc3OEMxMy45NDggNS44MTczMiAxMy44OTk5IDUuOTQyNjggMTMuODExNiA2LjAzNzQ2TDguOTc4MjIgMTAuODcwOEM4Ljg4NDQ3IDEwLjk2NDQgOC43NTczOSAxMS4wMTcgOC42MjQ4OSAxMS4wMTdDOC40OTIzOCAxMS4wMTcgOC4zNjUzIDEwLjk2NDQgOC4yNzE1NSAxMC44NzA4TDMuNDM4MjIgNi4wMzc0NkMzLjM0NDU5IDUuOTQzNzEgMy4yOTE5OSA1LjgxNjYzIDMuMjkxOTkgNS42ODQxM0MzLjI5MTk5IDUuNTUxNjMgMy4zNDQ1OSA1LjQyNDU1IDMuNDM4MjIgNS4zMzA4WiIgZmlsbD0iIzg1ODY4RSIvPgo8L3N2Zz4K);
  background-repeat: no-repeat;
  background-position: right 8px center;
  padding: 6px 16px;
  padding-left: 0px;
  padding-right: 24px;
  margin-left: 0;
  margin-right: auto;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
`

const PillContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  width: 100%;

  ${({theme}) => theme.mediaQueries.tablet} {
    gap: 8px;
  }
`;

const Pill = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-radius: var(--Spacing-Border-Radius-spacing_radius_small, 4px);
  border: 1px solid var(--Colors-Border-color_border_primary, #EDEDF0);
  background: var(--Colors-Buttons-color_button_transparent, rgba(255, 255, 255, 0.10));
  white-space: nowrap;
  font-family: var(--Typography-Font-FontFamily-font_family_proxima_nova, proxima-nova);
  font-size: 12px;
`;

export default CalendarComponent;